import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { I18nextProvider } from 'react-i18next';

import { Outlet, createRootRoute } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';

import {
  AppHeader,
  AppSidebar,
  AuthErrorHandler,
  AuthProvider,
  ErrorBoundary,
  Footer,
  SnackbarProvider,
} from '@/components';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { usePlatform } from '@/data/hooks/api/usePlatform';
import authService from '@/data/services/auth.service';
import i18n from '@/utils/plugins/i18n.plugin';
import { readSidebarOpen, writeSidebarOpen } from '@/utils/sidebarStorage';

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  const tokenInitializedRef = useRef(false);
  const platformsLoadedRef = useRef(false);
  const { loadPlatforms } = usePlatform();
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarOpen);

  const handleSidebarOpenChange = (open: boolean) => {
    setSidebarOpen(open);
    writeSidebarOpen(open);
  };

  useEffect(() => {
    // * Initialize token from URL if present, but only once
    if (authService.hasTokenInUrl() && !tokenInitializedRef.current) {
      console.log('🔑 Root component initializing token from URL...');
      tokenInitializedRef.current = true;
      authService.initializeToken();
    }
  }, []);

  useEffect(() => {
    // * Load platforms on app startup, but only once
    if (!platformsLoadedRef.current) {
      console.log('🚀 Loading platforms on app startup...');
      platformsLoadedRef.current = true;
      loadPlatforms();
    }
  }, [loadPlatforms]);

  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <SnackbarProvider>
          <AuthProvider>
            <AuthErrorHandler>
              <TooltipProvider delayDuration={0}>
                <SidebarProvider
                  open={sidebarOpen}
                  onOpenChange={handleSidebarOpenChange}
                  // Rail replié : 12px + case icône 36px + 12px + 1px de bordure droite.
                  style={{ '--sidebar-width-icon': '61px' } as CSSProperties}
                >
                  <AppSidebar />
                  <SidebarInset className="bg-page-background">
                    <div className="flex h-svh flex-col overflow-hidden">
                      <AppHeader />
                      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                        <div className="flex flex-1 flex-col gap-4 p-4">
                          <Outlet />
                        </div>
                        <Footer />
                      </div>
                    </div>
                  </SidebarInset>
                </SidebarProvider>
              </TooltipProvider>
            </AuthErrorHandler>
          </AuthProvider>
        </SnackbarProvider>
      </I18nextProvider>
      <TanStackRouterDevtools />
    </ErrorBoundary>
  );
}
